/*
 * Product pages' "Book and add to basket" (snippets/product-form.liquid, wired by assets/booking-calendar-inline.js).
 *
 * Steps on the page: what (step 1), how often (the subscription step), the clinic appointment, then the form's
 * own button. With a clinic visit chosen, no appointment in the basket yet and a time picked, the button reads
 * BOOK AND ADD TO BASKET and a press books first: the calendar's booking runs (the phone pop-up when asked),
 * then the button is pressed again for the theme, so quantity, subscription, ticked offers and the theme's own
 * click script all run as usual, and the page goes to the basket. If the test fails to add, the basket shows
 * the appointment with "Let's add some tests". When the test is already in the basket, or the customer is
 * changing a booked appointment, the button reads BOOK APPOINTMENT and only books. Without a time picked, or
 * with an appointment already in the basket, the button is the theme's Add to basket and nothing here touches
 * the press.
 *
 * attach(opts) -> { sync(), waiting(), booked(), failed() } or null without a form and button.
 *   opts: { win, form, atc, cartUrl, canBook(): bool, addAfter(): bool, inFlight(): bool, book(): void }
 *   canBook()  a press will book (the calendar has a clinic and time, and nothing booked yet)
 *   addAfter() add the test after booking (false: book only)
 *   inFlight() the calendar is booking right now (the phone pop-up is open or the hold is under way)
 *   book()     start the calendar's booking (its own guards decide whether it goes ahead)
 *   booked()   the calendar held the slot: add the test (unless book only) and go; false when no press waited
 *   failed()   the booking did not go ahead
 * The form carries form.bookAndAdd = { busy(), willBook() } for the sticky bar's add counting (pressKind), and
 * the document gets 'bookandadd:booked' once the appointment is in (assets/sticky-atc.js confirmBooked).
 * Pure helpers are exported for tests/book-and-add.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root && root.document && !root.BookAndAdd) root.BookAndAdd = api;
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  var LABEL = 'BOOK AND ADD TO BASKET';
  var LABEL_BOOK_ONLY = 'BOOK APPOINTMENT';
  var AFTER_ADD_MS = 1500;    // the add has answered: the theme's own follow-up is not needed to leave
  var FALLBACK_MS = 20000;    // an add that never announces itself (a failure, a theme change)
  var ADD_URL = /\/cart\/add(\.js)?(\?|$)/;

  function isOurs(text) { return text === LABEL || text === LABEL_BOOK_ONLY; }

  /** The label to show, and the theme's label to put back later (whatever the theme last drew). */
  function nextLabel(current, themeLabel, book, bookOnly) {
    var theme = !isOurs(current) ? current : themeLabel || current;
    return { label: book ? (bookOnly ? LABEL_BOOK_ONLY : LABEL) : theme, themeLabel: theme };
  }

  /** How the sticky bar counts a press: 'skip' (swallowed), 'calendar' (a booking first) or 'plain'. */
  function pressKind(ba) {
    if (!ba) return 'plain';
    if (ba.busy()) return 'skip';
    return ba.willBook() ? 'calendar' : 'plain';
  }

  function attach(opts) {
    var win = opts.win;
    var doc = win.document;
    var form = opts.form;
    var atc = opts.atc;
    if (!form || !atc) return null;
    var cartUrl = opts.cartUrl || '/cart';

    var pressWaiting = false;   // a press is waiting on the booking
    var addThisTime = true;     // decided at the press: the calendar clears its change state while booking
    var letThrough = false;     // our own press after the booking: the theme's add
    var leaving = false;        // the booking is done and the test sent: no second add, on to the basket
    var gone = false;
    var timers = [];
    var perf = null;
    var themeLabel = '';
    var drawing = false;

    var ask = function (fn, fallback) { try { return !!fn(); } catch (e) { return fallback; } };
    var willBook = function () { return ask(opts.canBook, false); };
    var addAfter = function () { return opts.addAfter ? ask(opts.addAfter, true) : true; };
    // Empire redraws the button with $button.text(...), which drops the label span: look it up every time.
    var labelEl = function () { return (atc.querySelector && atc.querySelector('.atc-button--text')) || atc; };
    // The theme's "added to your basket" flyout would show while the basket page loads: hidden on the way out
    // (booking-calendar-inline.css), shown again for plain adds after coming back.
    var LEAVING_CLASS = 'bookandadd-leaving';
    var leavingClass = function (on) {
      try { var c = doc.documentElement.classList; if (on) c.add(LEAVING_CLASS); else c.remove(LEAVING_CLASS); } catch (e) { /* cosmetic */ }
    };

    function sync() {
      var el = labelEl();
      var current = String(el.textContent || '').trim();
      var next = nextLabel(current, themeLabel, willBook() && !atc.disabled, !addAfter());
      themeLabel = next.themeLabel;
      if (current === next.label) return;
      drawing = true;
      el.textContent = next.label;
      drawing = false;
    }

    function stopWaiting() {
      timers.forEach(function (t) { win.clearTimeout(t); });
      timers = [];
      if (perf) { try { perf.disconnect(); } catch (e) { /* gone */ } perf = null; }
      win.removeEventListener('cartcount:update', go);
    }

    function go() {
      if (gone) return;
      gone = true;
      stopWaiting();
      win.location.assign(cartUrl);
    }

    function booked() {
      if (!pressWaiting) return false;
      pressWaiting = false;
      leaving = true;
      announce();
      if (!addThisTime) { go(); return true; }
      leavingClass(true);
      win.addEventListener('cartcount:update', go);   // the theme's "added" signal
      if (win.PerformanceObserver) {
        try {
          perf = new win.PerformanceObserver(function (list) {
            var done = list.getEntries().some(function (en) { return ADD_URL.test(en.name); });
            if (done) timers.push(win.setTimeout(go, AFTER_ADD_MS));
          });
          perf.observe({ type: 'resource' });
        } catch (e) { perf = null; /* the event or the fallback */ }
      }
      timers.push(win.setTimeout(go, FALLBACK_MS));
      // A real press: the form's click script picks the right banner, and the browser submits it the usual way.
      letThrough = true;
      try { atc.click(); } finally { letThrough = false; }
      return true;
    }

    function failed() { pressWaiting = false; }

    // The appointment is in the basket: the sticky bar confirms the press it is holding as an add.
    function announce() {
      try { if (win.CustomEvent) doc.dispatchEvent(new win.CustomEvent('bookandadd:booked', { detail: { add: addThisTime } })); } catch (e) { /* counting only */ }
    }

    // Every press of the button (and Enter in the form) arrives here as a submit, before the theme's handler.
    doc.addEventListener('submit', function (e) {
      if (e.target !== form || letThrough) return;
      // Leaving for the basket, or a booking under way for an earlier press (the calendar flips the label back
      // the moment the slot is held, before it reports booked()): this press is that one, never a second add.
      if (leaving || (pressWaiting && ask(opts.inFlight || function () { return true; }, true))) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      if (!willBook()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      pressWaiting = true;
      addThisTime = addAfter();
      opts.book();
    }, true);

    // Back from the basket out of the browser's page cache: start again, and don't go to the basket a second time.
    win.addEventListener('pageshow', function (e) {
      if (!e || !e.persisted) return;
      stopWaiting();
      pressWaiting = false;
      leaving = false;
      gone = false;
      leavingClass(false);
      sync();
    });

    // Keep the label right when the theme redraws the button (variant changes) or a slot is picked or cleared.
    if (win.MutationObserver && atc.nodeType) {
      new win.MutationObserver(function () { if (!drawing) sync(); })
        .observe(atc, { childList: true, characterData: true, subtree: true });
    }
    form.addEventListener('change', function () { win.setTimeout(sync, 0); });
    doc.addEventListener('variant-change', function () { win.setTimeout(sync, 0); }, true);

    form.bookAndAdd = {
      // A press that waited on a pop-up the customer got out of (a failed skip) is not busy any more.
      busy: function () { return leaving || (pressWaiting && ask(opts.inFlight || function () { return true; }, true)); },
      willBook: willBook,
    };

    return { sync: sync, waiting: function () { return pressWaiting; }, booked: booked, failed: failed };
  }

  return { LABEL: LABEL, LABEL_BOOK_ONLY: LABEL_BOOK_ONLY, nextLabel: nextLabel, pressKind: pressKind, attach: attach };
});
